import { useMemo } from "react";

import { parseISO } from "date-fns";

import { Header } from "components/elements";
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
  VerticalTabs,
} from "components/layout";
import { NO_VALUE_PLACEHOLDER } from "src/constants";
import {
  RECOMMENDATION_TYPES,
  TiVoPresentationAttributes,
  TiVoPresentationConfig,
} from "src/hooks/tivoApps";
import { formatDate } from "utils/date";

import {
  PRESENTATION_ATTRIBUTES_LABELS,
  PRESENTATION_BOOLEAN,
} from "../../constants";
import { getPresentationBooleanEntries } from "../../utils";
import { TEXTS } from "../constants";
import { Countries } from "./Countries";
import styles from "./PresentationConfigs.scss";
import { TechnicalText } from "./TechnicalText";

export type PresentationConfigsProps = {
  configs: TiVoPresentationConfig[];
};

function getTabName(config: TiVoPresentationConfig) {
  if (isDefaultTab(config)) {
    return TEXTS.PRESENTATION.DEFAULT_CONFIG_LABEL;
  }
  const matchValue =
    (config.countries.length > 0
      ? config.countries
      : config.platformSupportedFeatures) || [];
  return matchValue.join(", ") || NO_VALUE_PLACEHOLDER;
}

function isDefaultTab(config: TiVoPresentationConfig) {
  return !config.countries.length && !config.platformSupportedFeatures?.length;
}

function parseAttributes(
  attrs: TiVoPresentationAttributes
): { label: string; value: string }[] {
  const {
    name,
    publishDateTime,
    recommendationsType,
    platformSupportedApps,
    ...rest
  } = attrs;
  return [
    ...[
      name
        ? {
            label: PRESENTATION_ATTRIBUTES_LABELS.name,
            value: name || NO_VALUE_PLACEHOLDER,
          }
        : undefined,
    ],
    ...[
      recommendationsType
        ? {
            label: PRESENTATION_ATTRIBUTES_LABELS.recommendationsType,
            value: RECOMMENDATION_TYPES[recommendationsType],
          }
        : undefined,
    ],
    ...[
      platformSupportedApps && platformSupportedApps.length > 0
        ? {
            label: PRESENTATION_ATTRIBUTES_LABELS.platformSupportedApps,
            value: platformSupportedApps.join(", "),
          }
        : undefined,
    ],
    ...parseBooleanAttributes(rest),
    ...parseNumberAttributes(rest),
    publishDateTime
      ? {
          label: PRESENTATION_ATTRIBUTES_LABELS.publishDateTime,
          value: formatDate(parseISO(String(publishDateTime))),
        }
      : undefined,
  ].filter((v) => v !== undefined);
}

function parseBooleanAttributes(attrs: TiVoPresentationAttributes) {
  return getPresentationBooleanEntries(attrs).map(({ key, value }) => ({
    label: PRESENTATION_ATTRIBUTES_LABELS[key],
    value: value ? PRESENTATION_BOOLEAN.on : PRESENTATION_BOOLEAN.off,
  }));
}

function parseNumberAttributes(attrs: TiVoPresentationAttributes) {
  const keys: (keyof TiVoPresentationAttributes)[] = [
    "sortOrder",
    "myAppsRangeGroup",
    "browsePlaybackSortOrder",
    "postPublishDaysConsideredNew",
  ];
  return keys
    .map((key) => {
      const attr = attrs[key] as number | null | undefined;
      if (attr === null || attr === undefined) {
        return undefined;
      }
      return {
        label: PRESENTATION_ATTRIBUTES_LABELS[key],
        value: String(attr),
      };
    })
    .filter((v) => v !== undefined);
}

export function PresentationConfigs({ configs }: PresentationConfigsProps) {
  const tabNames = useMemo(
    () =>
      configs.map((config) => ({
        default: isDefaultTab(config),
        name: getTabName(config),
        noRemove: true,
      })),
    [configs]
  );

  const renderMatchBySection = (config: TiVoPresentationConfig) => {
    if (isDefaultTab(config)) {
      return null;
    }

    if (config.countries.length) {
      return (
        <div className={styles.subsection}>
          <Header color="secondary" bold type="h5" uppercase>
            {TEXTS.PRESENTATION.MATCH_BY_COUNTRIES_SUBS_HEADER}
          </Header>
          <Countries countries={config.countries} />
        </div>
      );
    }
    if (config.platformSupportedFeatures?.length) {
      return (
        <div className={styles.subsection}>
          <Header color="secondary" bold type="h5" uppercase>
            {TEXTS.PRESENTATION.MATCH_BY_FEATURES_SUBS_HEADER}
          </Header>
          <Table>
            <TableHeader>
              <TableHeaderColumn>
                {TEXTS.PRESENTATION.FEATURE_COL_HEADER}
              </TableHeaderColumn>
            </TableHeader>
            <TableBody>
              {config.platformSupportedFeatures.map((feature) => (
                <TableRow key={feature}>
                  <TableRowColumn>
                    <TechnicalText>{feature}</TechnicalText>
                  </TableRowColumn>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
    }
    return null;
  };

  return (
    <VerticalTabs
      isAddButtonDisplayed={false}
      tabNames={tabNames}
      dataTestId="presentation-configs-tabs"
    >
      {(tabIndex: number) => {
        const config = configs[tabIndex];
        if (!config) {
          return null;
        }
        const attrs = parseAttributes(config.attributes);
        if (attrs.length === 0) {
          attrs.push({
            label: NO_VALUE_PLACEHOLDER,
            value: NO_VALUE_PLACEHOLDER,
          });
        }
        return (
          <div>
            {renderMatchBySection(config)}
            <div className={styles.subsection}>
              <Header color="secondary" bold type="h5" uppercase>
                {TEXTS.PRESENTATION.ATTRS_SUBS_HEADER}
              </Header>
              <Table>
                <TableHeader>
                  {[
                    TEXTS.PRESENTATION.LABEL_COL_HEADER,
                    TEXTS.PRESENTATION.VALUE_COL_HEADER,
                  ].map((header) => (
                    <TableHeaderColumn
                      key={header}
                      className={styles.attrsTableCol}
                    >
                      {header}
                    </TableHeaderColumn>
                  ))}
                </TableHeader>
                <TableBody>
                  {attrs.map(({ label, value }) => (
                    <TableRow key={label}>
                      <TableRowColumn>{label}</TableRowColumn>
                      <TableRowColumn>{value}</TableRowColumn>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        );
      }}
    </VerticalTabs>
  );
}
